import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Bug, X, Image as ImageIcon, Paperclip } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { getRecentErrors } from "../lib/errorLog";

function detectDevice() {
  if (typeof navigator === "undefined") return "";
  const mobile = /iPhone|iPad|iPod|Android|webOS|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent,
  );
  return mobile ? "mobile" : "desktop";
}

/** Resize an image File → JPEG data URL bounded at 1600px on the long
 *  side @ q0.7. Keeps most bug screenshots under ~500 KB of base64 while
 *  still capturing enough detail for the reporter to point at a broken
 *  element. Preserves aspect ratio; skips resizing if already small. */
async function compressImageFile(file, maxSide = 1600, quality = 0.7) {
  const dataUrl = await new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(r.error);
    r.readAsDataURL(file);
  });
  const img = await new Promise((resolve, reject) => {
    const el = new Image();
    el.onload = () => resolve(el);
    el.onerror = () => reject(new Error("Couldn't read that image"));
    el.src = dataUrl;
  });
  const longSide = Math.max(img.naturalWidth, img.naturalHeight);
  if (longSide <= maxSide) {
    // Already small — but re-encode as JPEG if it's PNG/HEIC to cut
    // size; skip for genuinely small (< 200KB) images to keep sharpness.
    if (file.size < 200_000 && (file.type === "image/jpeg" || file.type === "image/jpg")) {
      return { dataUrl, width: img.naturalWidth, height: img.naturalHeight, bytes: file.size };
    }
  }
  const scale = longSide > maxSide ? maxSide / longSide : 1;
  const w = Math.round(img.naturalWidth * scale);
  const h = Math.round(img.naturalHeight * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, w, h);
  const jpeg = canvas.toDataURL("image/jpeg", quality);
  // Rough byte estimate for the preview text: (base64 length * 3) / 4
  const bytes = Math.round((jpeg.length - jpeg.indexOf(",") - 1) * 3 / 4);
  return { dataUrl: jpeg, width: w, height: h, bytes };
}

/**
 * "Report a problem" affordance — a tiny ghost button that opens a modal with
 * a 3-field form plus auto-captured context (URL, device, user agent, user
 * type via cookie on the backend), and an optional screenshot attachment
 * so reporters can show exactly what they saw. Usable by anonymous or
 * logged-in users.
 */
export default function BugReportButton({ variant = "ghost", showLabel = true, testId = "bug-report-btn" }) {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [what, setWhat] = useState("");
  const [doing, setDoing] = useState("");
  const [email, setEmail] = useState("");
  // Screenshot attachment state — data URL (compressed JPEG) plus a
  // tiny thumbnail-friendly `preview` so the user can confirm what
  // they're about to send. `null` when nothing attached.
  const [screenshot, setScreenshot] = useState(null); // { dataUrl, width, height, bytes }
  const [attaching, setAttaching] = useState(false);
  const fileInputRef = useRef(null);

  const close = () => {
    if (submitting) return;
    setOpen(false);
    setScreenshot(null);
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    // Reset the input so re-picking the same file still fires onChange.
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (!file) return;
    if (!/^image\//.test(file.type)) {
      toast.error("Only images can be attached");
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      toast.error("Image too large (max 20 MB before compression)");
      return;
    }
    setAttaching(true);
    try {
      const compressed = await compressImageFile(file);
      setScreenshot(compressed);
    } catch (err) {
      toast.error(err?.message || "Couldn't read that image");
    } finally {
      setAttaching(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!what.trim()) {
      toast.error("Please describe what went wrong");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/bug-reports", {
        what_went_wrong: what.trim(),
        what_were_you_doing: doing.trim(),
        email: email.trim(),
        page_url: typeof window !== "undefined"
          ? window.location.origin + location.pathname + location.search
          : "",
        device_type: detectDevice(),
        user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
        console_errors: getRecentErrors(),
        screenshot_data_url: screenshot?.dataUrl || "",
      });
      toast.success("Thanks — report sent. We'll take a look.");
      setWhat("");
      setDoing("");
      setEmail("");
      setScreenshot(null);
      setOpen(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't submit the report — try again?");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Button
        type="button"
        size="sm"
        variant={variant}
        onClick={() => setOpen(true)}
        className="gap-1.5"
        data-testid={testId}
        aria-label="Report a problem"
      >
        <Bug weight="duotone" className="h-4 w-4" />
        {showLabel && <span className="hidden sm:inline">Report a problem</span>}
      </Button>

      {/* Portal to <body>: the navbar's backdrop-blur creates a CSS
          containing block in Chromium (Android), which would trap this
          position:fixed overlay inside the 64px header — the modal then
          renders above the viewport. Portaling escapes it everywhere. */}
      {open && createPortal(
        <div
          className="fixed inset-0 z-[60] bg-black/60 grid place-items-end sm:place-items-center p-0 sm:p-4"
          onClick={close}
          data-testid="bug-report-modal"
        >
          <form
            onSubmit={onSubmit}
            onClick={(e) => e.stopPropagation()}
            onPaste={async (e) => {
              // Let the browser handle text paste normally — we only
              // hijack when the clipboard has an image. Common flow:
              // user takes a screenshot on desktop → paste into modal.
              if (screenshot) return;
              const items = e.clipboardData?.items || [];
              for (const it of items) {
                if (it.kind === "file" && /^image\//.test(it.type)) {
                  e.preventDefault();
                  const file = it.getAsFile();
                  if (!file) return;
                  setAttaching(true);
                  try {
                    const compressed = await compressImageFile(file);
                    setScreenshot(compressed);
                    toast.success("Screenshot attached from clipboard");
                  } catch (err) {
                    toast.error(err?.message || "Couldn't read that image");
                  } finally {
                    setAttaching(false);
                  }
                  return;
                }
              }
            }}
            className="w-full sm:max-w-md rounded-t-3xl sm:rounded-2xl bg-card border border-border p-6 space-y-4 shadow-xl max-h-[85dvh] overflow-y-auto pb-[max(1.5rem,var(--safe-area-inset-bottom,env(safe-area-inset-bottom)))]"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-primary font-semibold">
                  <Bug weight="fill" className="h-3.5 w-3.5" /> Report a problem
                </div>
                <h2 className="mt-1 font-display font-bold text-2xl tracking-tight">What happened?</h2>
              </div>
              <button
                type="button"
                onClick={close}
                className="h-8 w-8 grid place-items-center rounded-full hover:bg-muted flex-none"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-2">
              <Label htmlFor="bug-what">What went wrong? <span className="text-muted-foreground font-normal">(required)</span></Label>
              <Textarea
                id="bug-what"
                rows={3}
                value={what}
                onChange={(e) => setWhat(e.target.value)}
                placeholder="e.g. Couldn't save my Friday stop — spun forever"
                required
                data-testid="bug-what-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="bug-doing">What were you trying to do? <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <Textarea
                id="bug-doing"
                rows={2}
                value={doing}
                onChange={(e) => setDoing(e.target.value)}
                placeholder="e.g. Adding a new event on the weekly schedule"
                data-testid="bug-doing-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="bug-email">Your email <span className="text-muted-foreground font-normal">(optional — if you'd like a reply)</span></Label>
              <Input
                id="bug-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
                data-testid="bug-email-input"
              />
            </div>

            {/* Optional screenshot — client-side compressed to a JPEG
                data URL so the POST body stays small. We show a thumbnail
                preview + a Remove button so the reporter can confirm
                (and swap) what they're about to send. */}
            <div className="space-y-2">
              <Label>Screenshot <span className="text-muted-foreground font-normal">(optional — worth 1,000 words)</span></Label>
              {screenshot ? (
                <div
                  className="relative rounded-lg overflow-hidden border border-border bg-muted/30"
                  data-testid="bug-screenshot-preview-wrap"
                >
                  <img
                    src={screenshot.dataUrl}
                    alt="Screenshot preview"
                    className="block w-full max-h-56 object-contain bg-black/5"
                    data-testid="bug-screenshot-preview"
                  />
                  <div className="flex items-center justify-between px-3 py-2 text-[11px] text-muted-foreground border-t border-border bg-card">
                    <span>
                      {screenshot.width}×{screenshot.height} ·{" "}
                      {(screenshot.bytes / 1024).toFixed(0)} KB
                    </span>
                    <button
                      type="button"
                      onClick={() => setScreenshot(null)}
                      className="inline-flex items-center gap-1 text-primary hover:text-primary/80 font-semibold"
                      data-testid="bug-screenshot-remove"
                    >
                      <X className="h-3 w-3" /> Remove
                    </button>
                  </div>
                </div>
              ) : (
                <label
                  htmlFor="bug-screenshot-input"
                  className={`flex items-center justify-center gap-2 rounded-lg border border-dashed border-border bg-muted/20 hover:bg-muted/40 transition-colors px-4 py-4 cursor-pointer text-sm text-muted-foreground ${attaching ? "opacity-60 pointer-events-none" : ""}`}
                  data-testid="bug-screenshot-picker"
                >
                  {attaching ? (
                    <>Processing image…</>
                  ) : (
                    <>
                      <Paperclip weight="duotone" className="h-4 w-4 text-primary" />
                      <span className="font-medium text-foreground">Attach a screenshot</span>
                      <span className="hidden sm:inline text-muted-foreground">— tap to choose or paste from clipboard</span>
                    </>
                  )}
                </label>
              )}
              <input
                ref={fileInputRef}
                id="bug-screenshot-input"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFileChange}
                data-testid="bug-screenshot-file-input"
              />
            </div>

            <p className="text-[11px] text-muted-foreground leading-relaxed">
              We'll automatically include the page you were on and your device type so we can reproduce the issue.
            </p>

            <div className="flex gap-2 pt-1">
              <Button
                type="submit"
                disabled={submitting}
                className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 rounded-full h-11"
                data-testid="bug-submit-btn"
              >
                {submitting ? "Sending…" : "Send report"}
              </Button>
              <Button type="button" variant="outline" onClick={close} disabled={submitting} className="rounded-full">
                Cancel
              </Button>
            </div>
          </form>
        </div>,
        document.body,
      )}
    </>
  );
}
